/**
 * Marketing / Social Post generator page.
 *
 * Renders three pixel-perfect shareable posts (Square 1080×1080,
 * Portrait 1080×1350, Story 1080×1920) that cover Facebook, Instagram,
 * WhatsApp Status, and LinkedIn. Each post can be downloaded as a PNG
 * with a single click via `html-to-image`.
 *
 * Route: /marketing/social-post (public — no login required so it can be
 * shared with the marketing team).
 */
import React, { useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import { toast } from 'sonner';
import {
  Buildings, Calculator, Receipt, ShoppingCart, ChartLineUp, FilePdf,
  ShieldCheck, Sparkle, Robot, Users, Wrench, ArrowSquareOut,
  Phone, EnvelopeSimple, Globe, WhatsappLogo, DownloadSimple, InstagramLogo,
  FacebookLogo, Cloud, Package, FilePdf as FilePdfIcon
} from '@phosphor-icons/react';

// ------- Editable contact block (kept in one place so you can customise once) -------
const CONTACT = {
  companyName: 'SGK Apps',
  tagline: 'Makers of BizCore™ ERP',
  phone: '+971 50 559 6941',
  whatsapp: '+971505596941',
  email: 'info@sgkapps.com',
  website: 'https://www.bizcore.sgkapps.com',
  cta: 'Book a free 30-min demo',
};

const FEATURES = [
  { icon: Calculator, title: 'Full Accounting', sub: 'Double-entry ledger · VAT · GST · Corporate Tax' },
  { icon: Receipt, title: 'Sales & Quotations', sub: 'Part invoicing · Auto SoA · PDF quotes' },
  { icon: ShoppingCart, title: 'Purchases', sub: 'RFQ · PO · GRN · Bills · 3-way match' },
  { icon: Users, title: 'CRM & Contacts', sub: 'Customer 360 · Timeline · Statements' },
  { icon: Package, title: 'Inventory & Ops', sub: 'Stock · Projects · Tasks · Timesheets' },
  { icon: Robot, title: 'AI-Powered', sub: 'Auto agreements · Smart summaries' },
  { icon: FilePdf, title: 'Branded PDFs', sub: 'QR codes · revision tracking · dark mode' },
  { icon: ChartLineUp, title: 'Reports & KPIs', sub: 'P&L · Trial balance · Custom builder' },
  { icon: ShieldCheck, title: 'UAE + India Ready', sub: 'FTA VAT · GST · e-Invoice · Audit trail' },
  { icon: Cloud, title: 'Cloud & Backups', sub: 'Auto snapshots · GDrive sync · uptime' },
  { icon: Wrench, title: 'Service Reports', sub: 'Field ops · attachments · signatures' },
  { icon: Buildings, title: 'Multi-Company', sub: 'Accountant Hub · unlimited tenants' },
];

// ------- Design tokens (locked to look print-ready) -------
const BRAND = {
  ink: '#0b1220',
  gold: '#d4a84b',
  bgFrom: '#0b1220',
  bgVia: '#111a2d',
  bgTo: '#0d1524',
  soft: 'rgba(255,255,255,0.08)',
  hair: 'rgba(212,168,75,0.35)',
};

// A single feature pill — dense, screenshot-friendly
const FeaturePill = ({ Icon, title, sub, compact = false }) => (
  <div className={`flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.04] ${compact ? 'p-3' : 'p-4'} backdrop-blur`}>
    <div className={`shrink-0 rounded-xl bg-[${BRAND.gold}]/15 border border-[${BRAND.gold}]/40 ${compact ? 'p-1.5' : 'p-2'}`} style={{ background: 'rgba(212,168,75,0.15)', borderColor: 'rgba(212,168,75,0.4)' }}>
      <Icon size={compact ? 18 : 22} weight="duotone" style={{ color: BRAND.gold }} />
    </div>
    <div className="min-w-0">
      <div className={`font-semibold text-white leading-tight ${compact ? 'text-[13px]' : 'text-[15px]'}`}>{title}</div>
      <div className={`text-white/60 leading-tight mt-0.5 ${compact ? 'text-[10.5px]' : 'text-[12px]'}`}>{sub}</div>
    </div>
  </div>
);

// Contact strip shared across all three posts
const ContactStrip = ({ dense = false }) => (
  <div className={`flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-white/85 ${dense ? 'text-[12px]' : 'text-[13.5px]'}`}>
    <span className="inline-flex items-center gap-1.5"><WhatsappLogo size={16} weight="fill" style={{ color: '#25d366' }} /> {CONTACT.whatsapp}</span>
    <span className="inline-flex items-center gap-1.5"><Phone size={14} weight="fill" style={{ color: BRAND.gold }} /> {CONTACT.phone}</span>
    <span className="inline-flex items-center gap-1.5"><EnvelopeSimple size={14} weight="fill" style={{ color: BRAND.gold }} /> {CONTACT.email}</span>
    <span className="inline-flex items-center gap-1.5"><Globe size={14} weight="fill" style={{ color: BRAND.gold }} /> {CONTACT.website.replace(/^https?:\/\//, '')}</span>
  </div>
);

// Small logo/wordmark
const Wordmark = ({ scale = 1 }) => (
  <div className="inline-flex items-center gap-3" style={{ transform: `scale(${scale})`, transformOrigin: 'left center' }}>
    <div
      className="w-11 h-11 rounded-xl flex items-center justify-center font-black text-[22px]"
      style={{ background: `linear-gradient(135deg, ${BRAND.gold} 0%, #b98a2f 100%)`, color: BRAND.ink, boxShadow: '0 8px 24px rgba(212,168,75,0.35)' }}
    >B</div>
    <div>
      <div className="text-white font-bold text-[22px] leading-none tracking-tight">BizCore<span style={{ color: BRAND.gold }}>™</span></div>
      <div className="text-white/50 text-[10.5px] uppercase tracking-[0.22em] mt-1">Enterprise ERP</div>
    </div>
  </div>
);

// ============================================================
// Square 1080 × 1080 — FB feed, IG feed, WhatsApp Status
// ============================================================
const SquarePost = React.forwardRef((_, ref) => (
  <div
    ref={ref}
    style={{
      width: 1080,
      height: 1080,
      background: `radial-gradient(1200px 700px at 20% 0%, rgba(212,168,75,0.18) 0%, transparent 60%), linear-gradient(135deg, ${BRAND.bgFrom} 0%, ${BRAND.bgVia} 50%, ${BRAND.bgTo} 100%)`,
      color: 'white',
      padding: 56,
      position: 'relative',
      overflow: 'hidden',
    }}
    className="rounded-none"
  >
    {/* Subtle grid texture */}
    <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)', backgroundSize: '48px 48px', pointerEvents: 'none' }} />
    <div style={{ position: 'absolute', top: -160, right: -160, width: 520, height: 520, borderRadius: '50%', background: 'radial-gradient(circle, rgba(212,168,75,0.28) 0%, transparent 70%)', filter: 'blur(20px)' }} />

    <div className="relative flex items-start justify-between">
      <Wordmark />
      <div className="text-right">
        <div className="text-white/60 text-[11px] uppercase tracking-[0.2em]">Run your business, not your spreadsheets</div>
      </div>
    </div>

    <div className="relative mt-10">
      <div className="text-[13px] uppercase tracking-[0.28em]" style={{ color: BRAND.gold }}>Modular ERP · Multi-Tenant · UAE + India Ready</div>
      <h1 className="mt-3 font-black leading-[0.98] tracking-tight" style={{ fontSize: 82, color: 'white' }}>
        One app.<br />
        <span style={{ color: BRAND.gold }}>Every</span> corner<br />
        of your business.
      </h1>
      <p className="mt-4 text-white/70 max-w-[820px]" style={{ fontSize: 20, lineHeight: 1.4 }}>
        Accounting, Sales, Purchases, Inventory, HR, Projects, AI Agreements, Compliance — all in one beautifully-designed suite. Ship invoices, close books, and get paid — faster.
      </p>
    </div>

    <div className="relative mt-9 grid grid-cols-3 gap-3">
      {FEATURES.slice(0, 9).map((f) => (
        <FeaturePill key={f.title} Icon={f.icon} title={f.title} sub={f.sub} compact />
      ))}
    </div>

    <div className="relative mt-9 rounded-2xl border p-5 flex items-center justify-between" style={{ borderColor: BRAND.hair, background: 'rgba(212,168,75,0.08)' }}>
      <div>
        <div className="text-white/60 text-[11px] uppercase tracking-[0.22em]">Ready to switch?</div>
        <div className="mt-1 font-bold text-[24px]">{CONTACT.cta}</div>
      </div>
      <div className="text-right">
        <div className="text-white font-semibold text-[15px]">{CONTACT.companyName}</div>
        <div className="text-white/60 text-[12px]">{CONTACT.tagline}</div>
      </div>
    </div>

    <div className="absolute left-0 right-0 px-14" style={{ bottom: 38 }}>
      <ContactStrip />
    </div>
  </div>
));

// ============================================================
// Portrait 1080 × 1350 — IG feed / Facebook feed (max engagement)
// ============================================================
const PortraitPost = React.forwardRef((_, ref) => (
  <div
    ref={ref}
    style={{
      width: 1080,
      height: 1350,
      background: `radial-gradient(1200px 900px at 80% 0%, rgba(212,168,75,0.22) 0%, transparent 55%), linear-gradient(135deg, ${BRAND.bgFrom} 0%, ${BRAND.bgVia} 50%, ${BRAND.bgTo} 100%)`,
      color: 'white',
      padding: 56,
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)', backgroundSize: '48px 48px', pointerEvents: 'none' }} />
    <div style={{ position: 'absolute', top: -140, left: -140, width: 460, height: 460, borderRadius: '50%', background: 'radial-gradient(circle, rgba(212,168,75,0.30) 0%, transparent 70%)', filter: 'blur(20px)' }} />

    <div className="relative flex items-start justify-between">
      <Wordmark />
      <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] rounded-full px-3 py-1.5" style={{ background: 'rgba(212,168,75,0.15)', color: BRAND.gold, border: `1px solid ${BRAND.hair}` }}>
        <Sparkle size={14} weight="fill" /> New · 2026 Release
      </span>
    </div>

    <div className="relative mt-14">
      <div className="text-[13px] uppercase tracking-[0.28em]" style={{ color: BRAND.gold }}>Everything your business runs on — unified.</div>
      <h1 className="mt-3 font-black leading-[0.96] tracking-tight" style={{ fontSize: 96, color: 'white' }}>
        The ERP that<br />
        <span style={{ color: BRAND.gold }}>respects</span><br />
        your time.
      </h1>
      <p className="mt-6 text-white/70 max-w-[900px]" style={{ fontSize: 22, lineHeight: 1.45 }}>
        BizCore replaces 6+ tools with one clean, modern workspace. Built for SMBs and accountants across UAE and India, tax-ready out of the box.
      </p>
    </div>

    <div className="relative mt-10 grid grid-cols-2 gap-3.5">
      {FEATURES.slice(0, 10).map((f) => (
        <FeaturePill key={f.title} Icon={f.icon} title={f.title} sub={f.sub} />
      ))}
    </div>

    <div className="relative mt-8 rounded-2xl border p-5 flex items-center justify-between" style={{ borderColor: BRAND.hair, background: 'rgba(212,168,75,0.08)' }}>
      <div>
        <div className="text-white/60 text-[12px] uppercase tracking-[0.22em]">Talk to us</div>
        <div className="mt-1 font-bold text-[26px]">{CONTACT.cta}</div>
      </div>
      <div className="text-right">
        <div className="text-white font-semibold text-[16px]">{CONTACT.companyName}</div>
        <div className="text-white/60 text-[13px]">{CONTACT.tagline}</div>
      </div>
    </div>

    <div className="absolute left-0 right-0 px-14" style={{ bottom: 34 }}>
      <ContactStrip />
    </div>
  </div>
));

// ============================================================
// Story 1080 × 1920 — IG / WhatsApp / FB Status
// ============================================================
const StoryPost = React.forwardRef((_, ref) => (
  <div
    ref={ref}
    style={{
      width: 1080,
      height: 1920,
      background: `radial-gradient(900px 1100px at 50% -10%, rgba(212,168,75,0.28) 0%, transparent 55%), linear-gradient(180deg, ${BRAND.bgFrom} 0%, ${BRAND.bgVia} 50%, ${BRAND.bgTo} 100%)`,
      color: 'white',
      padding: 64,
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)', backgroundSize: '52px 52px', pointerEvents: 'none' }} />

    <div className="relative flex items-center justify-center pt-6">
      <Wordmark scale={1.15} />
    </div>

    <div className="relative mt-24 text-center">
      <div className="text-[14px] uppercase tracking-[0.32em]" style={{ color: BRAND.gold }}>Modular ERP · UAE + India</div>
      <h1 className="mt-5 font-black leading-[0.94] tracking-tight mx-auto" style={{ fontSize: 128, color: 'white', maxWidth: 900 }}>
        Run<br />
        your <span style={{ color: BRAND.gold }}>whole</span><br />
        business.
      </h1>
      <p className="mt-8 text-white/70 mx-auto" style={{ fontSize: 26, lineHeight: 1.42, maxWidth: 880 }}>
        Accounting · Sales · Inventory · HR · Projects · AI Agreements · Tax — all in one place, beautifully designed.
      </p>
    </div>

    <div className="relative mt-14 space-y-3">
      {FEATURES.slice(0, 8).map((f) => (
        <FeaturePill key={f.title} Icon={f.icon} title={f.title} sub={f.sub} />
      ))}
    </div>

    <div className="absolute left-14 right-14" style={{ bottom: 220 }}>
      <div className="rounded-2xl border p-6 text-center" style={{ borderColor: BRAND.hair, background: 'rgba(212,168,75,0.10)' }}>
        <div className="text-white/60 text-[13px] uppercase tracking-[0.24em]">Ready to switch?</div>
        <div className="mt-2 font-black text-[38px]" style={{ color: BRAND.gold }}>{CONTACT.cta}</div>
        <div className="mt-4 text-white/85 text-[17px]">{CONTACT.companyName} · {CONTACT.tagline}</div>
      </div>
    </div>

    <div className="absolute left-0 right-0 px-14 text-center" style={{ bottom: 90 }}>
      <div className="flex flex-col items-center gap-3 text-white/85 text-[17px]">
        <span className="inline-flex items-center gap-2"><WhatsappLogo size={22} weight="fill" style={{ color: '#25d366' }} /> {CONTACT.whatsapp}</span>
        <span className="inline-flex items-center gap-2"><EnvelopeSimple size={20} weight="fill" style={{ color: BRAND.gold }} /> {CONTACT.email}</span>
        <span className="inline-flex items-center gap-2"><Globe size={20} weight="fill" style={{ color: BRAND.gold }} /> {CONTACT.website.replace(/^https?:\/\//, '')}</span>
      </div>
    </div>
  </div>
));

// ============================================================
// Page wrapper — preview + download buttons
// ============================================================
export default function SocialPostPage() {
  const squareRef = useRef(null);
  const portraitRef = useRef(null);
  const storyRef = useRef(null);
  const [busy, setBusy] = useState('');

  const download = async (node, filename, key) => {
    if (!node) return;
    setBusy(key);
    try {
      const dataUrl = await toPng(node, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: BRAND.ink,
      });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success(`Downloaded ${filename}`);
    } catch (err) {
      console.error(err);
      toast.error('Could not export image');
    } finally {
      setBusy('');
    }
  };

  // Renders a single post node to a data-URL PNG at 2x resolution and
  // returns it — used to compose the multi-page PDF export below.
  const renderPng = async (node) => {
    if (!node) return null;
    return toPng(node, {
      pixelRatio: 2,
      cacheBust: true,
      backgroundColor: BRAND.ink,
    });
  };

  // Multi-page PDF — one A4 landscape/portrait page per creative size.
  // Uses the raw pixel dimensions of each post as the jsPDF page size so
  // the PNG maps 1:1 (no cropping or resampling artefacts).
  const downloadAllAsPdf = async () => {
    setBusy('pdf');
    try {
      const pages = [
        { node: squareRef.current, w: 1080, h: 1080, label: 'Square 1:1 (Facebook · Instagram · WhatsApp Status)' },
        { node: portraitRef.current, w: 1080, h: 1350, label: 'Portrait 4:5 (Instagram Feed · Facebook Feed)' },
        { node: storyRef.current, w: 1080, h: 1920, label: 'Story 9:16 (Instagram Story · WhatsApp Status · FB Story)' },
      ];
      // Kick off first page so we can init jsPDF with matching dimensions
      const firstPng = await renderPng(pages[0].node);
      if (!firstPng) throw new Error('Nothing to render');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'px', format: [pages[0].w, pages[0].h], compress: true });
      pdf.addImage(firstPng, 'PNG', 0, 0, pages[0].w, pages[0].h, undefined, 'FAST');

      for (let i = 1; i < pages.length; i += 1) {
        const p = pages[i];
        const png = await renderPng(p.node);
        if (!png) continue;
        pdf.addPage([p.w, p.h], p.w > p.h ? 'landscape' : 'portrait');
        pdf.addImage(png, 'PNG', 0, 0, p.w, p.h, undefined, 'FAST');
      }

      pdf.save('bizcore-marketing-post.pdf');
      toast.success('Downloaded bizcore-marketing-post.pdf');
    } catch (err) {
      console.error(err);
      toast.error('Could not export PDF');
    } finally {
      setBusy('');
    }
  };

  // Single-post PDF export (uses the post's exact pixel size so the
  // shared file is print-ready). Cheaper than the combined PDF.
  const downloadOneAsPdf = async (node, w, h, filename, key) => {
    if (!node) return;
    setBusy(key);
    try {
      const png = await renderPng(node);
      if (!png) throw new Error('Nothing to render');
      const pdf = new jsPDF({ orientation: w > h ? 'landscape' : 'portrait', unit: 'px', format: [w, h], compress: true });
      pdf.addImage(png, 'PNG', 0, 0, w, h, undefined, 'FAST');
      pdf.save(filename);
      toast.success(`Downloaded ${filename}`);
    } catch (err) {
      console.error(err);
      toast.error('Could not export PDF');
    } finally {
      setBusy('');
    }
  };

  const openWA = () => {
    const msg = encodeURIComponent(
      `*BizCore™ ERP — one app for your whole business.*\n\n` +
      `✓ Accounting · Sales · Purchases · Inventory\n` +
      `✓ CRM · HR · Projects · AI Agreements\n` +
      `✓ UAE VAT + India GST + Corporate Tax ready\n` +
      `✓ Branded PDFs · Custom reports · Multi-company\n\n` +
      `${CONTACT.cta}\n` +
      `📞 ${CONTACT.whatsapp}\n` +
      `✉️ ${CONTACT.email}\n` +
      `🌐 ${CONTACT.website}`
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  const cardBase = 'rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden';

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white" data-testid="social-post-page">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-zinc-500">Marketing</div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight text-zinc-900 mt-1">Shareable social posts</h1>
            <p className="text-zinc-600 text-sm mt-1 max-w-xl">
              Ready-made, pixel-perfect creatives for Facebook, Instagram, and WhatsApp. Preview below, hit Download for the exact size you need.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={CONTACT.website}
              target="_blank" rel="noreferrer"
              className="text-xs font-semibold text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1.5"
            ><ArrowSquareOut size={14} /> Live app</a>
          </div>
        </div>

        {/* Quick share buttons */}
        <div className="mt-6 flex flex-wrap items-center gap-2">
          <button
            onClick={downloadAllAsPdf}
            disabled={busy === 'pdf'}
            data-testid="download-all-pdf-btn"
            className="inline-flex items-center gap-2 rounded-full bg-rose-600 text-white font-semibold text-sm px-4 py-2 hover:bg-rose-700 transition disabled:opacity-60"
          >
            <FilePdfIcon size={16} weight="fill" /> {busy === 'pdf' ? 'Building PDF…' : 'Download all as PDF'}
          </button>
          <button onClick={openWA} data-testid="share-whatsapp-btn" className="inline-flex items-center gap-2 rounded-full bg-[#25d366] text-white font-semibold text-sm px-4 py-2 hover:opacity-90 transition">
            <WhatsappLogo size={16} weight="fill" /> Share WhatsApp text
          </button>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(CONTACT.website)}`}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-[#1877f2] text-white font-semibold text-sm px-4 py-2 hover:opacity-90 transition"
          ><FacebookLogo size={16} weight="fill" /> Share to Facebook</a>
          <a
            href="https://www.instagram.com/" target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-tr from-[#f58529] via-[#dd2a7b] to-[#8134af] text-white font-semibold text-sm px-4 py-2 hover:opacity-90 transition"
          ><InstagramLogo size={16} weight="fill" /> Open Instagram</a>
        </div>

        {/* Post previews */}
        <div className="mt-10 space-y-10">
          {/* Square */}
          <section className={cardBase}>
            <div className="flex items-center justify-between px-5 py-3 border-b bg-zinc-50">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">1 : 1 · Square</div>
                <div className="text-sm font-semibold text-zinc-900">1080 × 1080 — Facebook · Instagram · WhatsApp Status</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => downloadOneAsPdf(squareRef.current, 1080, 1080, 'bizcore-social-square.pdf', 'sq-pdf')}
                  disabled={busy === 'sq-pdf'}
                  data-testid="download-square-pdf-btn"
                  className="inline-flex items-center gap-2 rounded-lg border border-zinc-300 text-zinc-800 font-semibold text-sm px-3.5 py-2 hover:bg-zinc-50 transition disabled:opacity-60"
                >
                  <FilePdfIcon size={14} weight="fill" className="text-rose-600" /> {busy === 'sq-pdf' ? 'PDF…' : 'PDF'}
                </button>
                <button
                  onClick={() => download(squareRef.current, 'bizcore-social-square-1080.png', 'sq')}
                  disabled={busy === 'sq'}
                  data-testid="download-square-btn"
                  className="inline-flex items-center gap-2 rounded-lg bg-zinc-900 text-white font-semibold text-sm px-3.5 py-2 hover:bg-zinc-800 transition disabled:opacity-60"
                >
                  <DownloadSimple size={14} weight="bold" /> {busy === 'sq' ? 'Rendering…' : 'PNG'}
                </button>
              </div>
            </div>
            <div className="p-4 flex justify-center bg-zinc-100">
              <div style={{ transform: 'scale(0.55)', transformOrigin: 'top center', width: 1080, height: 1080 }}>
                <SquarePost ref={squareRef} />
              </div>
            </div>
          </section>

          {/* Portrait */}
          <section className={cardBase}>
            <div className="flex items-center justify-between px-5 py-3 border-b bg-zinc-50">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">4 : 5 · Portrait</div>
                <div className="text-sm font-semibold text-zinc-900">1080 × 1350 — Instagram Feed · Facebook Feed (best engagement)</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => downloadOneAsPdf(portraitRef.current, 1080, 1350, 'bizcore-social-portrait.pdf', 'pt-pdf')}
                  disabled={busy === 'pt-pdf'}
                  data-testid="download-portrait-pdf-btn"
                  className="inline-flex items-center gap-2 rounded-lg border border-zinc-300 text-zinc-800 font-semibold text-sm px-3.5 py-2 hover:bg-zinc-50 transition disabled:opacity-60"
                >
                  <FilePdfIcon size={14} weight="fill" className="text-rose-600" /> {busy === 'pt-pdf' ? 'PDF…' : 'PDF'}
                </button>
                <button
                  onClick={() => download(portraitRef.current, 'bizcore-social-portrait-1080x1350.png', 'pt')}
                  disabled={busy === 'pt'}
                  data-testid="download-portrait-btn"
                  className="inline-flex items-center gap-2 rounded-lg bg-zinc-900 text-white font-semibold text-sm px-3.5 py-2 hover:bg-zinc-800 transition disabled:opacity-60"
                >
                  <DownloadSimple size={14} weight="bold" /> {busy === 'pt' ? 'Rendering…' : 'PNG'}
                </button>
              </div>
            </div>
            <div className="p-4 flex justify-center bg-zinc-100">
              <div style={{ transform: 'scale(0.5)', transformOrigin: 'top center', width: 1080, height: 1350 }}>
                <PortraitPost ref={portraitRef} />
              </div>
            </div>
          </section>

          {/* Story */}
          <section className={cardBase}>
            <div className="flex items-center justify-between px-5 py-3 border-b bg-zinc-50">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">9 : 16 · Story</div>
                <div className="text-sm font-semibold text-zinc-900">1080 × 1920 — Instagram Story · WhatsApp Status · Facebook Story</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => downloadOneAsPdf(storyRef.current, 1080, 1920, 'bizcore-social-story.pdf', 'st-pdf')}
                  disabled={busy === 'st-pdf'}
                  data-testid="download-story-pdf-btn"
                  className="inline-flex items-center gap-2 rounded-lg border border-zinc-300 text-zinc-800 font-semibold text-sm px-3.5 py-2 hover:bg-zinc-50 transition disabled:opacity-60"
                >
                  <FilePdfIcon size={14} weight="fill" className="text-rose-600" /> {busy === 'st-pdf' ? 'PDF…' : 'PDF'}
                </button>
                <button
                  onClick={() => download(storyRef.current, 'bizcore-social-story-1080x1920.png', 'st')}
                  disabled={busy === 'st'}
                  data-testid="download-story-btn"
                  className="inline-flex items-center gap-2 rounded-lg bg-zinc-900 text-white font-semibold text-sm px-3.5 py-2 hover:bg-zinc-800 transition disabled:opacity-60"
                >
                  <DownloadSimple size={14} weight="bold" /> {busy === 'st' ? 'Rendering…' : 'PNG'}
                </button>
              </div>
            </div>
            <div className="p-4 flex justify-center bg-zinc-100">
              <div style={{ transform: 'scale(0.35)', transformOrigin: 'top center', width: 1080, height: 1920 }}>
                <StoryPost ref={storyRef} />
              </div>
            </div>
          </section>
        </div>

        <div className="mt-14 rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <div className="text-sm font-semibold text-amber-900">Edit the contact block</div>
          <div className="text-xs text-amber-800 mt-1">
            All three creatives read their contact details from a single <code>CONTACT</code> object at the top of
            <code className="mx-1 px-1 py-0.5 rounded bg-white border border-amber-200">/app/frontend/src/pages/marketing/SocialPostPage.js</code>.
            Update phone / email / website there and every post re-renders instantly.
          </div>
        </div>
      </div>
    </div>
  );
}
